import React, { type ReactNode } from 'react'
import { css } from '@emotion/core'
import PropTypes from 'prop-types'
import { Ellipsis, WhiteTheme, colors, useTheme, usePageWidth } from '@fs/zion-ui'
import { VisuallyHidden } from '@reach/visually-hidden'
import { useTranslation } from 'react-i18next'
import { i18n } from '@fs/zion-locale'
import { validFlavors, flavors, FLAGS } from '../config'
import { getLanguageName } from './helpers/languageHelpers'
import { useFlagsContext } from '../providers'
import { formatDuration, getEnv } from './helpers'

interface BadgeProps {
  label: string | ReactNode
  flavor?: string
  keyline?: string
  maxWidth?: string | number
  'aria-label'?: string
  [key: string]: unknown
}

interface DurationBadgeProps {
  duration: string | number
  customCss?: Record<string, string | number>
}

interface SessionDifficultyBadgeProps {
  sessionDifficulty: string
}

const badgeCss = css`
  background: ${colors.gray02};
  display: flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  padding: 2px 7px;
  &[key-line='left'] {
    padding-top: 2px;
    padding-inline-end: 5px;
    padding-bottom: 2px;
    padding-inline-start: 0px;
  }
  border-radius: 3.6px;
  text-transform: uppercase;
  height: 20px;
  font-weight: 700;
  font-size: 12px;

  background-color: var(--background-color);
  color: var(--color);
`

export const Badge = ({
  label,
  flavor = flavors.default,
  keyline,
  maxWidth,
  'aria-label': ariaLabel,
  ...props
}: BadgeProps): JSX.Element => {
  const getLabelText = (): string | ReactNode => {
    if (typeof label === 'string') return label
    if (label && typeof label === 'object' && 'props' in label) {
      return label.props?.children || label
    }
    return label
  }

  return (
    <div
      css={badgeCss}
      style={
        {
          '--background-color': flavors[flavor].solid,
          '--color': flavors[flavor].textColor || colors.gray00,
          maxWidth: maxWidth ?? 'fit-content',
        } as React.CSSProperties
      }
      {...props}
      key-line={keyline}
    >
      <Ellipsis aria-hidden="true">{label}</Ellipsis>
      <VisuallyHidden>{ariaLabel || getLabelText()}</VisuallyHidden>
    </div>
  )
}

Badge.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
  flavor: PropTypes.oneOf(validFlavors),
}

export const DurationBadge = ({ duration, customCss }: DurationBadgeProps): JSX.Element | null => {
  const [t] = useTranslation()
  const formattedDuration = formatDuration(duration)
  const [secondsStr = '0', minutesStr = '0', hoursStr = '0'] = String(formattedDuration)?.split(':').reverse() || []
  const seconds = Number(secondsStr)
  const minutes = Number(minutesStr)
  const hours = Number(hoursStr)
  const durationIsZero = Number(String(formattedDuration)?.replaceAll(':', '')) === 0
  const theme = useTheme()

  if (durationIsZero) return null

  const prettyDuration = `${hours > 0 ? `${hours}:` : ''}`
    .concat(`${String(minutes).padStart(2, '0')}:`)
    .concat(`${String(seconds).padStart(2, '0')}`)
  return (
    <Badge
      flavor={theme === 'night' ? 'charcoal' : 'platinum'}
      label={
        <time dateTime={`PT${hours}H${minutes}M${seconds}S`}>
          <WhiteTheme>{prettyDuration}</WhiteTheme>
        </time>
      }
      data-testid="badge-duration"
      css={customCss}
      aria-label={t('badge.duration.label', 'Video duration is {duration}', { duration: prettyDuration })}
    />
  )
}

export const SeriesBadge = (): JSX.Element => {
  const [t] = useTranslation()
  return (
    <Badge
      flavor="seafoam"
      label={t('card.tag.series', 'Series')}
      data-testid="badge-series"
      aria-label={t('badge.series', 'This session is part of a series')}
    />
  )
}

export const LanguageBadge = ({ contentLocale }: { contentLocale?: string }): JSX.Element | null => {
  const [t] = useTranslation()
  const userLocale = i18n.language
  const contentIsInUsersLanguage = contentLocale?.split('-')[0] === userLocale
  const theme = useTheme()
  const atWidth = usePageWidth()
  if (!contentLocale || contentIsInUsersLanguage || contentLocale === 'und') return null

  const translatedName = getLanguageName(contentLocale.split('-')[0])

  return (
    <Badge
      flavor={theme === 'night' ? 'charcoal' : 'platinum'}
      label={translatedName}
      data-testid={`badge-language-${contentLocale}`}
      aria-label={t('badge.language', 'This session language is {translatedName}', { translatedName })}
      maxWidth={atWidth({
        default: 90,
        xxs: 112,
        xs: 120,
        md: 80,
        lg: 112,
        xxl: 136,
      })}
    />
  )
}

export const LiveBadge = (): JSX.Element => {
  const [t] = useTranslation()
  return (
    <Badge
      flavor="lava"
      label={t('card.tag.live', 'Live')}
      data-testid="badge-live"
      aria-label={t('badge.live', 'This session is live')}
    />
  )
}
export const ReplayBadge = (): JSX.Element => {
  const [t] = useTranslation()
  return (
    <Badge
      flavor="lava"
      label={t('card.tag.replay', 'Replay')}
      data-testid="badge-replay"
      aria-label={t('badge.replay', 'This session is a replay')}
    />
  )
}

export const DraftBadge = (): JSX.Element => {
  const [t] = useTranslation()
  return (
    <Badge
      flavor="draft"
      label={t('card.tag.draft', 'Draft')}
      data-testid="badge-draft"
      aria-label={t('badge.draft', 'This session is in draft')}
    />
  )
}

export const SubmittedBadge = (): JSX.Element => {
  const [t] = useTranslation()
  return (
    <Badge
      flavor="submitted"
      label={t('card.tag.submitted', 'Submitted')}
      data-testid="badge-submitted"
      aria-label={t('badge.submitted', 'This session is submitted')}
    />
  )
}

export const EditableBadge = (): JSX.Element => {
  const [t] = useTranslation()
  return (
    <Badge
      flavor="draft"
      label={t('card.tag.editable', 'In Progress')}
      data-testid="badge-editable"
      aria-label={t('badge.progress', 'This session is in progress')}
    />
  )
}

export const AcceptedBadge = (): JSX.Element => {
  const [t] = useTranslation()
  return (
    <Badge
      flavor="accepted"
      label={t('card.tag.accepted', 'Accepted')}
      data-testid="badge-accepted"
      aria-label={t('badge.accepted', 'This session has been accepted')}
    />
  )
}

export const PublishedBadge = (): JSX.Element => {
  const [t] = useTranslation()
  return (
    <Badge
      flavor="pending"
      label={t('card.tag.published', 'Published')}
      data-testid="badge-published"
      aria-label={t('badge.published', 'This session is published')}
    />
  )
}

export const PendingBadge = (): JSX.Element => {
  const [t] = useTranslation()
  return <Badge flavor="pending" label={t('card.tag.pending', 'Pending')} data-testid="badge-pending" />
}

export const ApprovedBadge = (): JSX.Element => {
  const [t] = useTranslation()
  return <Badge flavor="approved" label={t('card.tag.approved', 'Approved')} data-testid="badge-approved" />
}

export const DeclinedBadge = (): JSX.Element => {
  const [t] = useTranslation()
  return <Badge flavor="declined" label={t('card.tag.declined', 'Declined')} data-testid="badge-declined" />
}

export const RevokedBadge = (): JSX.Element => {
  const [t] = useTranslation()
  return <Badge flavor="declined" label={t('card.tag.revoked', 'Revoked')} data-testid="badge-revoked" />
}

export const PromotionBadge = (): JSX.Element => {
  const [t] = useTranslation()
  return <Badge flavor="alllevels" label={t('card.tag.promotion', 'Promotion')} data-testid="badge-promotion" />
}

export const PDFBadge = (): JSX.Element => {
  const [t] = useTranslation()
  return <Badge flavor="alllevels" label={t('card.tag.pdf', 'PDF')} data-testid="badge-pdf" />
}

export const ComingSoonBadge = (): JSX.Element => {
  const [t] = useTranslation()
  const theme = useTheme()

  return (
    <Badge
      flavor={theme === 'night' ? 'charcoal' : 'platinum'}
      label={<WhiteTheme>{t('badge.coming-soon.label', 'Coming Soon')}</WhiteTheme>}
      aria-label={t('badge.coming-soon.label', 'Coming Soon')}
      data-testid="coming-soon-badge"
    />
  )
}

export const OnlineBadge = (): JSX.Element => {
  const [t] = useTranslation()
  return (
    <Badge
      flavor="online"
      label={t('upload-content.session-location.online.label', 'Online')}
      data-testid="badge-online"
      aria-label={t('badge.online', 'This session is online')}
    />
  )
}

export const InPersonBadge = (): JSX.Element => {
  const [t] = useTranslation()
  return (
    <Badge
      flavor="gold"
      label={t('upload-content.session-location.in-person.label', 'In person')}
      data-testid="badge-inperson"
      aria-label={t('badge.in-person', 'This session is in person')}
    />
  )
}

export const InPersonOnlyBadge = (): JSX.Element => {
  const [t] = useTranslation()
  return (
    <Badge
      flavor="gold"
      label={t('upload-content.session-location.in-person-only.label', 'This class is not available online')}
      data-testid="badge-inpersononly"
      aria-label={t('badge.in-person-only', 'This session is in person only')}
    />
  )
}

export const SessionDifficultyBadge = ({ sessionDifficulty }: SessionDifficultyBadgeProps): JSX.Element | null => {
  const [t] = useTranslation()

  const difficulties = {
    Beginner: { color: 'beginner', label: t('card.session-difficulty.beginner.label', 'Beginner') },
    Intermediate: { color: 'intermediate', label: t('card.session-difficulty.intermediate.label', 'Intermediate') },
    'Advanced/Professional': {
      color: 'advanced',
      label: t('card.session-difficulty.advanced.label', 'Advanced/Professional'),
    },
    All: { color: 'alllevels', label: t('card.session-difficulty.all.label', 'All Levels') },
  }
  const difficultyLabel = difficulties[sessionDifficulty].label

  if (!difficulties[sessionDifficulty]) {
    return null
  }

  return (
    <Badge
      flavor={difficulties[sessionDifficulty].color}
      label={difficultyLabel}
      data-testid="badge-difficulty"
      aria-label={t('badge.difficulty', 'This session has a skill level of {difficultyLabel}', { difficultyLabel })}
    />
  )
}

export const EnvironmentBadge = (): JSX.Element | null => {
  const { [FLAGS.PREVIEW]: previewFlag } = useFlagsContext()
  const environments = {
    integration: { flavor: 'bronze', label: 'Integration' },
    beta: { flavor: 'silver', label: 'Beta' },
    // www: // NOTE: we don't want to show this is prod
  }
  const env = getEnv()

  if (!environments[env] && !previewFlag.isOn) {
    return null
  }

  return (
    <>
      {previewFlag.isOn && <Badge flavor="gold" label="Conference Preview" data-testid="badge-preview" />}
      {environments[env] && (
        <Badge flavor={environments[env].flavor} label={environments[env].label} data-testid="badge-environment" />
      )}
    </>
  )
}
